import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent } from "./ui/card";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";

export default function ProgressChart({ checkins = [] }) {
  const data = useMemo(
    () =>
      checkins.map((c) => ({
        date: new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        weight: Number(c.weight_kg),
        energy: c.energy,
        mood: c.mood,
      })),
    [checkins]
  );

  const trend = useMemo(() => {
    if (data.length < 2) return { dir: "flat", delta: 0 };
    const delta = +(data[data.length - 1].weight - data[0].weight).toFixed(1);
    return {
      dir: delta < -0.1 ? "down" : delta > 0.1 ? "up" : "flat",
      delta,
    };
  }, [data]);

  const TrendIcon = trend.dir === "down" ? TrendingDown : trend.dir === "up" ? TrendingUp : Minus;
  const trendColor =
    trend.dir === "down"
      ? "text-emerald-700"
      : trend.dir === "up"
      ? "text-amber-700"
      : "text-emerald-900/60";

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="progress-chart">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              Your progress
            </div>
            <h3 className="font-display text-lg text-emerald-950 mt-1">Weight & wellbeing</h3>
          </div>
          <div className={`flex items-center gap-1 text-sm font-semibold ${trendColor}`} data-testid="weight-trend">
            <TrendIcon className="w-4 h-4" />
            {trend.dir === "flat" ? "Stable" : `${trend.delta} kg`}
          </div>
        </div>
        {data.length === 0 ? (
          <div className="text-sm text-emerald-900/60 py-6 text-center">
            Log your first check-in to start tracking progress.
          </div>
        ) : (
          <div style={{ width: "100%", height: 240, minHeight: 240 }}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data} margin={{ top: 5, right: 16, left: -12, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d1fae5" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#064e3b" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#064e3b" }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 10]} tick={{ fontSize: 11, fill: "#92400e" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="weight" stroke="#047857" strokeWidth={2.5} dot={{ r: 3 }} name="Weight (kg)" />
                <Line yAxisId="right" type="monotone" dataKey="energy" stroke="#fbbf24" strokeWidth={2} dot={{ r: 3 }} name="Energy" />
                <Line yAxisId="right" type="monotone" dataKey="mood" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} name="Mood" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
